import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../../utils/classNames';
import { get_dynamic_styles } from '../../../constant/DynamicStyles';

const SelectDropdown = ({
  options = [],
  value = null,
  onChange = () => {},
  placeholder = 'Select an option',
  searchPlaceholder = 'Find option...',
  className = '',
  disabled = false,
  showSearch = true,
  // Styling props
  variant = 'default', // 'default', 'minimal', 'dark', 'custom'
  size = 'md', // 'sm', 'md', 'lg'
  customStyles = {}, // For advanced customization
  customInlineStyles,
  button_icon,
  is_multiple = false,
  is_search_controlled = false,
  onSearchChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = !is_search_controlled
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && showSearch && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen, showSearch]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };

  // Normalize selected values for multi-select mode
  const selectedValues = is_multiple
    ? Array.isArray(value)
      ? value
      : value != null
      ? [value]
      : []
    : [];

  const isOptionSelected = (option) =>
    is_multiple
      ? selectedValues.includes(option.value)
      : value === option.value;

  const handleSelect = (option) => {
    if (is_multiple) {
      const currentlySelected = isOptionSelected(option);
      const nextValues = currentlySelected
        ? selectedValues.filter((v) => v !== option.value)
        : [...selectedValues, option.value];
      onChange(nextValues);
      setSearchTerm('');
      setHighlightedIndex(-1);
      return; // keep menu open in multi-select
    }

    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const selectedOption = options.find((option) => option.value === value);
  const selectedOptions = is_multiple
    ? options.filter((option) => selectedValues.includes(option.value))
    : [];

  // Style configurations
  const styles = {
    default: {
      button:
        'bg-white border border-gray-200 hover:border-gray-300 text-gray-900',
      dropdown: 'bg-white border border-gray-200 shadow-lg',
      search:
        'bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500',
      search_box: 'p-3 border-b border-gray-100',
      item: 'hover:bg-gray-50 focus:bg-gray-50 border-b border-gray-100 last:border-b-0',
      selected: 'bg-primary/5 text-primary',
      highlighted: 'bg-gray-50',
      text: 'text-gray-900',
      icon: 'text-primary',
      arrow: 'text-gray-400',
      empty: 'text-gray-500',
    },
    minimal: {
      button:
        'bg-transparent border-0 text-gray-700 right-0 focus:outline-none',
      dropdown: 'bg-white border border-gray-100 shadow-sm',
      search:
        'bg-gray-50 border border-gray-100 focus:ring-1 focus:ring-gray-200',
      search_box: 'p-3 border-b border-gray-100',
      item: 'hover:bg-gray-50 focus:bg-gray-50 border-b border-gray-50 last:border-b-0',
      selected: 'bg-gray-100 text-gray-900',
      highlighted: 'bg-gray-50',
      text: 'text-gray-700',
      icon: 'text-gray-500',
      arrow: 'text-gray-400',
      empty: 'text-gray-400',
    },
    dark: {
      button:
        'bg-gray-800 border border-gray-700 hover:border-gray-600 focus:border-blue-400 text-white',
      dropdown: 'bg-gray-800 border border-gray-700 shadow-xl',
      search:
        'bg-gray-700 border border-gray-600 focus:ring-2 focus:ring-blue-400 text-white',
      search_box: 'p-3 border-b border-gray-100',
      item: 'hover:bg-gray-700 focus:bg-gray-700 border-b border-gray-700 last:border-b-0',
      selected: 'bg-gray-700 text-white',
      highlighted: 'bg-gray-700',
      text: 'text-gray-200',
      icon: 'text-blue-400',
      arrow: 'text-gray-400',
      empty: 'text-gray-400',
    },
  };

  const sizes = {
    sm: {
      button: 'px-3 py-1 text-sm',
      dropdown: 'max-h-fit',
      search: 'pl-8 pr-3 py-1 text-sm',
      search_box: 'px-3 border-b border-gray-100',
      item: 'px-3 py-2 text-sm',
      arrow: 'w-4 h-4',
      icon: 'w-3 h-3',
    },
    md: {
      button: 'px-4 py-3 text-base',
      dropdown: 'max-h-80',
      search: 'pl-10 pr-4 py-2 text-sm',
      search_box: 'p-3 border-b border-gray-100',
      item: 'px-4 py-3 text-base',
      arrow: 'w-5 h-5',
      icon: 'w-4 h-4',
    },
    lg: {
      button: 'px-5 py-4 text-lg',
      dropdown: 'max-h-96',
      search: 'pl-12 pr-5 py-3 text-base',
      search_box: 'p-3 border-b border-gray-100',
      item: 'px-5 py-4 text-lg',
      arrow: 'w-6 h-6',
      icon: 'w-5 h-5',
    },
  };

  const currentStyle = { ...styles[variant], ...customStyles };
  const currentSize = sizes[size];

  return (
    <div className={cn('relative w-full', className)} ref={dropdownRef}>
      {/* Main Button */}
      <button
        type="button"
        className={cn(
          'w-full flex items-center justify-between rounded-lg focus:outline-none  transition-all duration-200 ease-in-out',
          currentStyle.button,
          currentSize.button,
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        style={get_dynamic_styles(customInlineStyles)}
      >
        <div className="flex  items-center justify-start gap-2 ">
          {button_icon && (
            <img
              src={button_icon}
              alt="flag image"
              className={cn('h-6 w-6 object-cover  ')}
            />
          )}
          {is_multiple ? (
            <span className="font-medium truncate">
              {selectedOptions.length > 0
                ? selectedOptions
                    .slice(0, 4)
                    .map((o) => o.label)
                    .join(', ') +
                  (selectedOptions.length > 4
                    ? ` +${selectedOptions.length - 4}`
                    : '')
                : placeholder}
            </span>
          ) : (
            <span className="font-medium truncate">
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          )}
        </div>

        <svg
          className={cn(
            'flex-none',
            currentSize.arrow,
            currentStyle.arrow,
            'transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute top-full left-0 mt-1 z-50 overflow-hidden w-max min-w-full rounded-lg',
            currentStyle.dropdown,
            currentSize.dropdown
          )}
        >
          {/* Search Input */}
          {showSearch && (
            <div
              className={cn(
                'p-3 border-b border-gray-100',
                currentStyle.search_box,
                currentSize.search_box
              )}
            >
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  ref={searchRef}
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    is_search_controlled && onSearchChange(e.target.value);
                    setHighlightedIndex(-1);
                  }}
                  className={cn(
                    'w-full border focus:outline-none focus:border-transparent rounded-md',
                    currentStyle.search,
                    currentSize.search
                  )}
                />
              </div>
            </div>
          )}

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              <div className="py-1">
                {filteredOptions.map((option, index) => (
                  <button
                    key={option.value}
                    type="button"
                    className={cn(
                      'w-full flex items-center gap-3 text-left transition-colors duration-150 focus:outline-none',
                      currentStyle.item,
                      currentSize.item,
                      isOptionSelected(option) && currentStyle.selected,
                      index === highlightedIndex && currentStyle.highlighted
                    )}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <span className={cn('flex-1 truncate', currentStyle.text)}>
                      {option.label}
                    </span>

                    {is_multiple && (
                      <input
                        type="checkbox"
                        checked={isOptionSelected(option)}
                        className=" rounded border-primary outline-none focus:outline-none ring-0 focus:ring-0"
                      />
                    )}
                    {isOptionSelected(option) && !is_multiple && (
                      <svg
                        className={cn(
                          currentSize.icon,
                          currentStyle.icon,
                          'flex-shrink-0'
                        )}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center">
                <svg
                  className={cn(
                    'mx-auto mb-2',
                    currentSize.icon,
                    currentStyle.empty
                  )}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33"
                  />
                </svg>
                <p className={cn('text-sm', currentStyle.empty)}>
                  No options found
                </p>
              </div>
            )}
          </div>

          {is_multiple && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100 bg-white">
              <button
                type="button"
                className={cn('text-sm text-gray-600 hover:text-gray-900')}
                onClick={() => onChange([])}
              >
                Clear
              </button>
              <button
                type="button"
                className={cn('text-sm text-primary hover:opacity-80')}
                onClick={() => setIsOpen(false)}
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectDropdown;
